import theme from 'app/theme';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { ReactComponent as VirtualChip } from 'app/shared-components/cards/assets/chip-virtual.svg';
import { ReactComponent as PhysicalChip } from 'app/shared-components/cards/assets/chip-physical.svg';
import { ReactComponent as SpeedchainLogo } from 'assets/Blue-SpeedChain-Logo.svg';
import { ReactComponent as MasterCardMetal } from 'assets/master-card-logo-metal.svg';
import { ReactComponent as SpeedchainLogoGray } from 'assets/Gray-SpeedChain-Logo.svg';
import { CardViewerRenderedFields, renderFields } from '@highnoteplatform/card-viewer';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { CardFormFactor, DefaultCardType, checkCardType } from 'app/shared-components/cards/CardTypes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { getCardTypeDescription } from 'app/shared-components/util/utils';
import { COPIED_TO_CLIPBOARD } from 'util/constants';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/store';
import { sentryMask } from 'sentryReplayPrivacy';
import { CardWrapper } from './Style';
import { clearCardData } from './cardUtils';

interface Props {
  isUsersCard?: boolean;
  clientToken: string;
  paymentCardId: string;
  name?: string | null | undefined;
  formFactor: CardFormFactor;
  sx?: object;
  zipcode?: string;
  blurCard?: boolean;
  isAdminLocked?: boolean;
  last4?: string | null | undefined;
}

const SpecificCard = ({
  sx,
  clientToken,
  paymentCardId,
  name,
  formFactor,
  zipcode,
  blurCard,
  last4,
  isAdminLocked = false,
  isUsersCard = false,
}: Props) => {
  const { t } = useTranslation();
  const { integration } = useAppSelector(selectReviewOnboardSlice);

  const CARD_TYPE_DESCRIPTION = useMemo(
    () => getCardTypeDescription(integration?.highnoteProduct?.type),
    [integration?.highnoteProduct?.type]
  );

  const defaultCardType: DefaultCardType = checkCardType(integration?.highnoteProduct?.type, formFactor);
  const textColor: string = defaultCardType.textColor === 'light' ? 'white' : theme.palette.text.secondary;
  const cardIsVirtual = formFactor === 'VIRTUAL';

  // SEE-1080: start masked. Card number shows last-4, expiration `**/**`, CVV `***`
  // until the user reveals via the eye toggle.
  const [showCardInfo, setShowCardInfo] = useState(false);
  const [show, setShow] = useState(false);
  // Gates the eye control: reveal is disabled until the pre-mounted card-number iframe
  // has loaded, so a reveal can never race the iframe (see comment on the refs below).
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // A fatal SDK error (mount rejected, card fetch / credential error) leaves the eye
  // disabled; this drives a tooltip so it reads as broken-with-recovery, not decorative.
  const [sdkError, setSdkError] = useState(false);
  const [openTooltip, setOpen] = useState({
    cardNumber: false,
    expirationDate: false,
    cvv: false,
    zipcode: false,
  });

  const hasMountedSdk = useRef(false);
  const sdkRef = useRef<CardViewerRenderedFields>();
  // toggleCardNumberMask is a postMessage the iframe silently drops until the Highnote app
  // inside is listening, and it *flips* the mask rather than setting it — so it can neither
  // be fired early nor blindly retried. There is no ready signal: even the iframe's `load`
  // event fires before the app inside listens (verified — a toggle fired at load time is
  // dropped). The only reliable toggles are human-time ones, so the eye control stays
  // disabled until `iframeLoaded` and every click maps to exactly one landed toggle,
  // keeping the iframe's mask in lockstep with showCardInfo.

  // When card is locked (blurCard), the viewer isn't the owner, or there's no client token,
  // force-mask. Icon state (`show`) must reset with showCardInfo or it desyncs (masked
  // content, "revealed" icon). SDK teardown happens in the mount effect's cleanup, whose
  // deps cover the same condition flips.
  useEffect(() => {
    if (!(isUsersCard && clientToken && !blurCard)) {
      setShowCardInfo(false);
      setShow(false);
    }
  }, [isUsersCard, clientToken, blurCard]);

  // SEE-1080: pre-mount the SDK iframes as soon as the drawer renders an unlocked card the
  // user owns, keeping them hidden behind the masked placeholders (display:none — iframes
  // still load). By the time the user clicks the eye, the iframe has been loaded for human
  // seconds, so the unmask postMessage in hideAndShow always lands. Mounting on reveal
  // instead raced the iframe load and lost on a cold cache (the "takes 2-3 clicks" bug),
  // and its loading overlay flashed over the card. useHighnoteCardViewer.ts still mounts
  // on demand, but its guards (paymentCardId check, rAF cancellation) are mirrored here.
  // SEE-1080/SEE-307: `!blurCard` is load-bearing — a locked card must never mount the
  // real PAN/exp/CVV iframes behind the cosmetic blur(6px). `paymentCardId` is too:
  // CardSheet renders with `paymentCardId ?? ''` before params resolve, and mounting on
  // the empty id wastes a card-viewer round trip and keys the eye gate to a bogus iframe.
  useEffect(() => {
    let rafId = 0;
    if (isUsersCard && clientToken && paymentCardId && !blurCard && !hasMountedSdk.current) {
      hasMountedSdk.current = true;

      // Use requestAnimationFrame to ensure DOM is fully rendered — cancelled on cleanup,
      // or an unmount mid-transition lets the SDK's `querySelector(...) ?? document.body`
      // fallback append authenticated iframes to the body with nothing to tear them down.
      rafId = requestAnimationFrame(() => {
        clearCardData().then(() => {
          ViewPaymentCardForSdk()
            .then((response) => {
              sdkRef.current = response;
              const iframe = document.querySelector<HTMLIFrameElement>('#cardNumber iframe');
              // If the iframe is missing, leave the eye disabled — a reveal couldn't work.
              iframe?.addEventListener('load', () => setIframeLoaded(true), { once: true });
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('❌ SDK mount failed for card:', paymentCardId, error);
              setSdkError(true);
            });
        });
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      sdkRef.current?.unmount?.();
      sdkRef.current = undefined;
      hasMountedSdk.current = false;
      clearCardData();
      setIframeLoaded(false);
      setSdkError(false);
      // A remount must land masked: the replacement iframe starts masked, so leaving
      // showCardInfo=true here puts the UI one flip out of phase with the iframe — with
      // no resync path, since toggleCardNumberMask flips rather than sets. Reachable
      // without touching the card (e.g. CardSheet refetches the clientToken when its
      // transactions date filter changes).
      setShowCardInfo(false);
      setShow(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsersCard, clientToken, blurCard, paymentCardId]);

  const cardInfoStyle = (fontSize: number | string) => ({
    fontSize: fontSize + 'px',
    color: textColor,
    fontWeight: '600',
    cursor: 'pointer',
  });

  const ViewPaymentCardForSdk = async () => {
    return await renderFields({
      clientToken: clientToken,
      paymentCardId: paymentCardId,
      enableClipboard: true,
      environment: process.env.REACT_APP_ENV === 'production' ? 'live' : 'test',

      onCopyToClipboardSuccess: ({ field }) => {
        setOpen({ ...openTooltip, [field]: true });
      },
      onError: (error: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('SDK error for card:', paymentCardId, error);
        // Card fetch / credential errors mean the viewer can't work — surface that on the
        // disabled eye. Clipboard failures are per-copy and don't invalidate the viewer.
        if ((error as { name?: string })?.name !== 'CopyToClipboardError') {
          setSdkError(true);
        }
      },
      elements: {
        cardNumber: {
          selector: '#cardNumber',
          styles: cardInfoStyle(28),
        },
        expirationDate: {
          selector: '#expirationDate',
          styles: cardInfoStyle(16),
        },
        cvv: {
          selector: '#cvv',
          styles: cardInfoStyle(16),
        },
      },
    });
  };

  // SEE-1080: toggle all three fields together. showCardInfo only switches which layer is
  // visible (placeholders vs the pre-mounted iframes); the PAN iframe additionally needs its
  // own mask flipped, one toggle per click. Only reachable once `iframeLoaded` (the eye is
  // disabled until then), so the toggle postMessage always lands.
  const hideAndShow = () => {
    sdkRef.current?.toggleCardNumberMask();
    setShowCardInfo((prev) => !prev);
    setShow((show) => !show);
  };

  return (
    <CardWrapper sx={sx} backgroundImage={defaultCardType.cardImage}>
      {blurCard && (
        <Chip
          label="Your card is locked"
          icon={<WarningAmberIcon fontSize="small" style={{ color: theme.palette.warning.dark }} />}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: theme.palette.warning.dark,
            backgroundColor: theme.palette.warning.light,
            opacity: 1,
            zIndex: 10,
          }}
        />
      )}
      <Box
        sx={{
          zIndex: 10,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          filter: blurCard ? 'blur(6px)' : 'blur(0px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {cardIsVirtual ? <VirtualChip /> : <PhysicalChip />}
              <Typography sx={{ pl: 1 }} variant="small" color={theme.palette.secondary.main}>
                {cardIsVirtual ? t('cardTypes.digital') : t('cardTypes.physical')}
              </Typography>
            </Box>
          </Box>
          {defaultCardType.textColor === 'light' ? (
            <SpeedchainLogo style={{ paddingTop: 2 }} />
          ) : (
            <SpeedchainLogoGray style={{ paddingTop: 2 }} />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h4" color={textColor} {...sentryMask}>
            {name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 2,
            }}
          >
            {/* Card number. The iframe container stays mounted while masked (SEE-1080:
                the SDK pre-mounts into it, hidden) — display:none still loads iframes. */}
            {!showCardInfo && (
              <Typography
                sx={{ display: 'flex', alignItems: 'center', height: 30 }}
                variant="medium"
                color={textColor}
              >
                <Box
                  component="span"
                  sx={{
                    ...cardInfoStyle(28),
                    pl: 1,
                    cursor: 'default',
                    fontWeight: 300,
                    letterSpacing: '-1px',
                  }}
                >
                  {`•••• •••• •••• `}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ...cardInfoStyle(28),
                    pl: 1,
                    cursor: 'default',
                    fontWeight: !blurCard && last4 ? 700 : 300,
                    letterSpacing: '-1px',
                  }}
                >
                  {blurCard ? '••••' : last4 ? last4 : '••••'}
                </Box>
              </Typography>
            )}
            <Tooltip
              title={COPIED_TO_CLIPBOARD}
              placement="top"
              leaveDelay={500}
              open={openTooltip.cardNumber}
              onClose={() => setOpen({ ...openTooltip, cardNumber: false })}
              arrow
            >
              <div id="cardNumber" style={{ display: showCardInfo ? 'block' : 'none' }} />
            </Tooltip>
            {/* SEE-1080: no reveal control on a locked card — matches the pre-mask behavior
                where the eye was a no-op on locked cards, and avoids the eye reading "revealed"
                while the SDK mount stays blocked by `!blurCard`. */}
            {isUsersCard && !blurCard && (
              <Tooltip
                title={sdkError ? 'Card details couldn’t load. Close and reopen the card to retry.' : ''}
                placement="top"
                arrow
              >
                {/* span: MUI tooltips don't fire on disabled buttons without a wrapper */}
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    sx={{
                      color: textColor,
                      backgroundColor: 'transparent !important',
                      p: 0.5,
                      '&.Mui-disabled': { color: textColor, opacity: 0.4 },
                    }}
                    disabled={!iframeLoaded || sdkError}
                    onClick={() => hideAndShow()}
                  >
                    {show ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              pt: 2,
            }}
          >
            {!showCardInfo && (
              <Typography
                sx={{ display: 'flex', alignItems: 'center', height: 30 }}
                variant="medium"
                color={textColor}
              >
                Exp{' '}
                <Box component="span" sx={{ pl: 1, ...cardInfoStyle(16), cursor: 'default' }}>
                  ••/••
                </Box>
              </Typography>
            )}
            <Tooltip
              title={COPIED_TO_CLIPBOARD}
              placement="top"
              leaveDelay={500}
              open={openTooltip.expirationDate}
              onClose={() => setOpen({ ...openTooltip, expirationDate: false })}
              arrow
            >
              <Typography
                sx={{
                  display: showCardInfo ? 'flex' : 'none',
                  alignItems: 'center',
                  height: 30,
                }}
                variant="medium"
                color={textColor}
              >
                Exp <div id="expirationDate"></div>
              </Typography>
            </Tooltip>

            {/* CVV */}
            {!showCardInfo && (
              <Typography
                sx={{ display: 'flex', alignItems: 'center', height: 30 }}
                variant="medium"
                color={textColor}
              >
                Code{' '}
                <Box
                  component="span"
                  sx={{ pl: 1, ...cardInfoStyle(16), cursor: 'default', fontWeight: '700' }}
                >
                  •••
                </Box>
              </Typography>
            )}
            <Tooltip
              title={COPIED_TO_CLIPBOARD}
              placement="top"
              leaveDelay={500}
              open={openTooltip.cvv}
              onClose={() => setOpen({ ...openTooltip, cvv: false })}
              arrow
            >
              <Typography
                sx={{ display: showCardInfo ? 'flex' : 'none', alignItems: 'center', height: 30 }}
                variant="medium"
                color={textColor}
              >
                Code <div id="cvv"></div>
              </Typography>
            </Tooltip>
            <Tooltip
              title={COPIED_TO_CLIPBOARD}
              placement="top"
              leaveDelay={500}
              open={openTooltip.zipcode}
              onClose={() => setOpen({ ...openTooltip, zipcode: false })}
              arrow
            >
              <Typography
                sx={{ display: 'flex', alignItems: 'center', height: 30 }}
                variant="medium"
                color={textColor}
              ></Typography>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography sx={{ opacity: 0.8 }} variant="small" color={textColor}>
              {CARD_TYPE_DESCRIPTION}
            </Typography>
            <MasterCardMetal />
          </Box>
        </Box>
        {/* SEE-1080: no loading overlay — the SDK pre-mounts hidden and the eye control
            is disabled until the iframes are loaded, so a reveal is always instant. */}
      </Box>
    </CardWrapper>
  );
};

export default SpecificCard;
